import React, { useEffect, useState } from "react";

import { useQuery } from "@tanstack/react-query";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";

import TemplateSettingsForm from "./TemplateSettingsForm";
import TemplateTimeline, {
  DEFAULT_START_TIME,
  GROUP_ID,
} from "./TemplateTimeline";
import FastAPI from "../../../api/FastAPI";
import { TemplateSlotObject } from "../../../api/FastAPI.types";
import { TIME_FORMAT_LONG } from "../../../constants/dateFormat";
import { AD_BREAK, TIMESLOT } from "../../../constants/media";
import useFAST from "../../../contexts/FASTContext";
import useFASTTemplateMutations from "../../../hooks/mutations/useFASTTemplateMutations";
import useMediaColorByFormat from "../../../hooks/useMediaColorByFormat";
import { computeStartTimeSlotItem } from "../../../utils/fast";
import Block from "../../block/Block";
import TemplateAssetLibrary from "../mediaLibrary/TemplateAssetLibrary";

import type { JSX } from "react";

import type { TemplateFormValues } from "./TemplateSettingsForm";
import type {
  AdBreakSlot,
  UpdatedTimeslotSlot,
} from "../../../api/FastAPI.types";
import type { FASTChannelRoutesParams } from "../../routes/FASTChannelRoutes";
import type { AllTemplateItems } from "../timeline/timeline.types";

function TemplateEdit(): JSX.Element {
  const { t } = useTranslation("editor-fast-channel");
  const [items, setItems] = useState<AllTemplateItems[]>([]);

  const { state } = useFAST();

  const { channelData } = state;

  const getMediaColor = useMediaColorByFormat();

  const { channelId, templateId } = useParams<
    FASTChannelRoutesParams & {
      templateId: string;
    }
  >();

  const dailyStartTime = channelData?.daily_start_time ?? DEFAULT_START_TIME;

  const MIN_TIME = moment(dailyStartTime, TIME_FORMAT_LONG)
    .add(1, "month")
    .valueOf();

  const isNewTemplate = templateId === "new";

  const getTemplateQuery = FastAPI.generateQuery({
    fn: FastAPI.getTemplate,
    params: [channelId, templateId],
  });

  const { data: template } = useQuery({
    ...getTemplateQuery,
    enabled: !isNewTemplate,
  });

  const { updateFASTTemplate } = useFASTTemplateMutations();

  const handleSubmit = (data: TemplateFormValues): void => {
    const updatedSlots = items.reduce(
      (acc: (AdBreakSlot | UpdatedTimeslotSlot)[], item) => {
        const time = moment(item.start_time).format(TIME_FORMAT_LONG);

        if (item.type === AD_BREAK) {
          acc.push({
            adbreak_uuid: item.mediaId,
            start_time: time,
            object: TemplateSlotObject.AdBreak,
            adbreak: {
              uuid: item.mediaId,
              name: item.title as string,
              duration: item.duration,
            },
          });
        }

        if (item.type === TIMESLOT && item.color) {
          acc.push({
            fast_channel_timeslot_uuid: item.mediaId,
            start_time: time,
            object: TemplateSlotObject.FastChannelTimeslot,
            fast_channel_timeslot: {
              uuid: item.mediaId,
              name: item.title as string,
              duration: item.duration,
              color: item.color,
            },
          });
        }

        return acc;
      },
      [],
    );

    updateFASTTemplate.mutate({
      updatedTemplate: { ...data, slots: updatedSlots },
      isNewTemplate,
    });
  };

  useEffect(() => {
    const itemList = template?.slots.reduce((acc: AllTemplateItems[], slot) => {
      const startTime = computeStartTimeSlotItem(
        slot.start_time,
        moment(MIN_TIME),
      );

      if (startTime) {
        if (slot.object === TemplateSlotObject.AdBreak) {
          acc.push({
            id: uuidv4(),
            mediaId: slot.adbreak_uuid,
            group: GROUP_ID,
            title: slot.adbreak.name,
            start_time: startTime,
            end_time: moment(startTime.valueOf() + slot.adbreak.duration),
            canMove: true,
            duration: slot.adbreak.duration,
            type: AD_BREAK,
            color: getMediaColor(AD_BREAK),
          });
        }

        if (slot.object === TemplateSlotObject.FastChannelTimeslot) {
          acc.push({
            id: uuidv4(),
            mediaId: slot.fast_channel_timeslot_uuid,
            group: GROUP_ID,
            title: slot.fast_channel_timeslot.name,
            start_time: startTime,
            end_time: moment(
              startTime.valueOf() + slot.fast_channel_timeslot.duration,
            ),
            canMove: true,
            duration: slot.fast_channel_timeslot.duration,
            type: TIMESLOT,
            color: slot.fast_channel_timeslot.color,
          });
        }
      }

      return acc;
    }, []);

    setItems(itemList ?? []);
  }, [getMediaColor, template, MIN_TIME]);

  return (
    <React.Fragment>
      <div className="mr-[0.78125rem] flex flex-col">
        <TemplateAssetLibrary channelId={channelId} />
      </div>
      <div className="mr-[0.78125rem] flex-1">
        <Block
          isFullSize
          bodyStyle="p-[0.3125rem]!"
          title={t("gridTemplateTitle")}
        >
          <TemplateTimeline items={items} updateItems={setItems} />
        </Block>
      </div>
      <TemplateSettingsForm
        items={items}
        onSubmit={handleSubmit}
        template={template}
      />
    </React.Fragment>
  );
}

export default TemplateEdit;
